/**
 * Shared utilities for reaction components
 * Contains common constants, types, and helper functions used by ReactionBar and ReactionSummaryDisplay
 */
import { MyReactions, PostReactionType, ReactionStats } from '__generated__/graphql/legacy/graphql';

/**
 * Formats a number for display using Intl.NumberFormat with compact notation
 * @param count - The number to format
 * @param locale - The locale to use for formatting (defaults to "en")
 * @returns Formatted string (e.g., "999", "1K", "1.2K", "1M")
 */
export const formatReactionCount = (count: number, locale: string = 'en'): string => {
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(count);
};

/**
 * Represents a selectable reaction emoji with all necessary data
 */
export interface ReactionEmoji {
  /** Emoji identifier for emoji-mart */
  id: string;
  /** Human-readable name of the emoji */
  name: string;
  /** GraphQL enum value for API calls */
  graphqlEnum: PostReactionType;
}

// Mapping from database reactionStats keys to emoji data
// Using snake_case keys to match database format and emoji-mart compatible identifiers
export const REACTIONS_SUMMARY_MAPPING: Record<string, ReactionEmoji> = {
  like: { id: '+1', name: 'thumbs up', graphqlEnum: PostReactionType.LIKE },
  heart_eyes: { id: 'heart_eyes', name: 'heart eyes', graphqlEnum: PostReactionType.HEART_EYES },
  open_mouth: { id: 'open_mouth', name: 'open mouth', graphqlEnum: PostReactionType.OPEN_MOUTH },
  joy: { id: 'joy', name: 'joy', graphqlEnum: PostReactionType.JOY },
  pensive: { id: 'pensive', name: 'pensive', graphqlEnum: PostReactionType.PENSIVE },
  tada: { id: 'tada', name: 'tada', graphqlEnum: PostReactionType.TADA },
  eyes: { id: 'eyes', name: 'eyes', graphqlEnum: PostReactionType.EYES },
  wave: { id: 'wave', name: 'wave', graphqlEnum: PostReactionType.WAVE },
} as const;

/**
 * Gets all available emojis from the reactions summary mapping
 * @returns Array of ReactionEmoji objects
 */
export const getAvailableEmojis = (): ReactionEmoji[] => {
  return Object.keys(REACTIONS_SUMMARY_MAPPING).map((key) => {
    const reactionKey = key as keyof typeof REACTIONS_SUMMARY_MAPPING;
    return REACTIONS_SUMMARY_MAPPING[reactionKey];
  });
};

/**
 * Normalized reaction data structure for UI components
 */
export interface DisplayReaction {
  count: number;
  id: string;
  name: string;
  hasReacted: boolean;
  reactionType: string;
  reactors?: string;
}

/**
 * Transforms reactionStats JSON object into normalized format for UI components
 * @param reactionStats - JSON object with reaction counts (e.g. {"like": 1, "heart_eyes": 2})
 * @param myReactions - Array of user's current reactions
 * @param currentUserName - Current user's name for optimistic (optional)
 */
export const transformReactionStats = (
  reactionStats: ReactionStats | undefined,
  myReactions: MyReactions | undefined,
  currentUserName?: string
): DisplayReaction[] => {
  if (!reactionStats) {
    return [];
  }

  const reactions = Object.entries(reactionStats)
    .map(([statsKey, reactionData]) => {
      // Get emoji data directly using database key
      const emojiData = REACTIONS_SUMMARY_MAPPING[statsKey];
      if (!emojiData) {
        return null; // Skip unknown reaction types
      }

      // Extract count and reactor from the new format
      const count = reactionData?.count || 0;
      let reactors = reactionData?.reactors;

      const reactionType = emojiData.graphqlEnum;
      const hasReacted = myReactions?.includes(reactionType) || false;

      // Handle optimistic display when current user has reacted
      if (hasReacted && currentUserName) {
        if (!reactors) {
          // No existing reactors, just show current user
          reactors = currentUserName;
        } else if (!reactors.includes(currentUserName)) {
          // Current user not in existing reactors list, add them
          reactors = `${currentUserName}, ${reactors}`;
        }
      }

      return {
        count,
        id: emojiData.id,
        name: emojiData.name,
        hasReacted,
        reactionType,
        reactors,
      };
    })
    .filter((reaction) => reaction !== null && reaction.count > 0) as DisplayReaction[];

  return reactions;
};
